import {
  AcceptInvitationRequestV1,
  InvitationTokenParamsV1,
  InvitationViewV1,
  LoginRequestV1,
  LoginStepResponseV1,
  MfaCodeRequestV1,
  NewPasswordRequestV1,
  PasswordResetAcceptedV1,
  PasswordResetConfirmV1,
  PasswordResetRequestV1,
  SessionStartedResponseV1,
  SsoCallbackQueryV1,
  SsoStartQueryV1,
  SsoStartRequestV1,
  SsoStartResponseV1,
  TwoFactorSetupBeginResponseV1,
} from "./auth.ts";
import {
  ConsoleUserListResponseV1,
  DeactivatedConsoleUserResponseV1,
  GrantConsoleUserPermissionsRequestV1,
  InvitationIssuedResponseV1,
  InvitationListResponseV1,
  InviteConsoleUserRequestV1,
  OperatorScopedRequestV1,
  OperatorScopeQueryV1,
  SetConsoleUserRoleRequestV1,
} from "./console-users.ts";
import { DocsAccessResponseV1 } from "./docs-access.ts";
import {
  FleetAuditQueryV1,
  FleetAuditResponseV1,
  FleetConfigureCustomerRequestV1,
  FleetCreateCustomerRequestV1,
  FleetCustomerParamsV1,
  FleetDestroyCustomerRequestV1,
  FleetHaltRequestV1,
  FleetInstallationDetailV1,
  FleetInstallationParamsV1,
  FleetListQueryV1,
  FleetListResponseV1,
  FleetPlanRequestV1,
  FleetPlanV1,
  FleetPublishRequestV1,
  FleetRejectRequestV1,
  FleetReleaseListResponseV1,
  FleetRequestAcceptedV1,
  FleetRequestListQueryV1,
  FleetRequestListResponseV1,
  FleetRequestResponseV1,
  FleetRequestParamsV1,
  FleetRequestV1,
  FleetRolloutParamsV1,
  FleetRolloutRequestV1,
  FleetRolloutResponseV1,
  FleetStepUpRequestV1,
} from "./fleet.ts";
import type { ExplorerApiErrorCode } from "./errors.ts";
import {
  IdentityProviderSettingsResponseV1,
  IdentityProviderTestResponseV1,
  IdentityProviderViewV1,
  PutIdentityProviderRequestV1,
} from "./identity-provider.ts";
import {
  INSTALLATION_CACHE_MAX_AGE_SECONDS,
  InstallationAdministrationV1,
  InstallationV1,
} from "./installation.ts";
import {
  AccountV1,
  MeResponseV1,
  MySettingsResponseV1,
  PasswordChangeV1,
  ProfileUpdateV1,
  TwoFactorDisableV1,
  TwoFactorEnrollV1,
  TwoFactorReplaceV1,
  UpdateMySettingsRequestV1,
} from "./me.ts";
import {
  ConfigureEnvironmentRequestV1,
  CreateCredentialRequestV1,
  DeactivatedPlatformAdministratorResponseV1,
  DeactivatePlatformAdministratorRequestV1,
  ResetPlatformAdministratorTwoFactorRequestV1,
  CreatedCredentialResponseV1,
  CreateOperatorRequestV1,
  CreateOrganizationRequestV1,
  InviteFirstAdministratorRequestV1,
  InvitePlatformAdministratorRequestV1,
  OperatorDeactivationRequestV1,
  OperatorLinkedResponseV1,
  OperatorOrganizationResponseV1,
  OrganizationCreatedResponseV1,
  PlatformAdministratorListResponseV1,
  PlatformAdministratorParamsV1,
  PlatformCredentialListResponseV1,
  PlatformCredentialParamsV1,
  PlatformCredentialV1,
  PlatformEnvironmentResponseV1,
  PlatformInvitationListQueryV1,
  PlatformInvitationListResponseV1,
  PlatformInvitationParamsV1,
  PlatformOperatorListResponseV1,
  PlatformOperatorParamsV1,
  PlatformOperatorV1,
  PlatformOrganizationAccessV1,
  PlatformOrganizationListQueryV1,
  PlatformOrganizationListResponseV1,
  PlatformOrganizationParamsV1,
  PlatformProductParamsV1,
  PutProductRequestV1,
  RevokeCredentialRequestV1,
  RevokePlatformInvitationRequestV1,
} from "./platform.ts";
import { PatchProductWindowRequestV1 } from "./integration.ts";
import { EXPLORER_ROUTES, IdParams, type ExplorerApiRoute } from "./routes.ts";

// The routes of the Explorer API served by `iam` and by the `operator-auth` platform interface
// (the `explorer` module's own routes are `EXPLORER_ROUTES`). Every `/explorer/v1` route is in
// one of these tables, which drive the OpenAPI document; the modules' contract tests check that
// the routes their Fastify plugins register and these tables are the same.

/** Public routes: rate limited per client IP, failing closed when the limiter cannot decide. */
const PUBLIC_ERRORS = [
  "invalid_request",
  "rate_limited",
  "server_error",
] as const satisfies readonly ExplorerApiErrorCode[];
const INVITATION_UNUSABLE = [
  "invitation_used",
  "invitation_expired",
  "invitation_revoked",
] as const;

/** Console user management: `users.manage` on the operator the request names. */
const MANAGE_READ = [
  "unauthenticated",
  "forbidden",
  "not_found",
  "invalid_request",
  "server_error",
] as const satisfies readonly ExplorerApiErrorCode[];
const MANAGE_WRITE = [...MANAGE_READ, "csrf_token_invalid"] as const;

/** Organization identity provider (always the caller's own organization). */
const IDP_WRITE = [
  "unauthenticated",
  "forbidden",
  "csrf_token_invalid",
  "not_found",
  "server_error",
] as const satisfies readonly ExplorerApiErrorCode[];

/** Platform routes: a caller without the platform permission gets `404`, never `403`. */
const PLATFORM_READ = [
  "unauthenticated",
  "not_found",
  "invalid_request",
  "server_error",
] as const satisfies readonly ExplorerApiErrorCode[];
const PLATFORM_WRITE = [...PLATFORM_READ, "csrf_token_invalid"] as const;

/** The code step, the mandatory set-up and the new-password step of a password login (design D5). */
const LOGIN_STEP_ERRORS = [
  ...PUBLIC_ERRORS,
  "mfa_challenge_expired",
] as const satisfies readonly ExplorerApiErrorCode[];
const LOGIN_STEP_NOTE =
  "Needs the login-step cookie of the 202 answer of POST /auth/login, POST /auth/login/new-password or POST /invitations/{token}/accept, from the same browser; without it (or after 3 minutes for the code and the new password, 10 for the set-up, or 3 wrong attempts) the answer is mfa_challenge_expired. Rate limited per client IP and per account.";
const STEP_DESCRIPTION =
  "The password was right but no session starts yet: next_step is mfa_required (enter the authenticator code, POST /auth/login/mfa), mfa_setup_required (set up an authenticator, POST /auth/login/two-factor-setup/begin then confirm) or new_password_required (the password was a temporary one: choose a new one, POST /auth/login/new-password).";

/** The session's own account in Settings (change add-console-account-settings). */
const ACCOUNT_WRITE = [
  "unauthenticated",
  "csrf_token_invalid",
  "invalid_request",
  "rate_limited",
  "server_error",
] as const satisfies readonly ExplorerApiErrorCode[];
const ACCOUNT_CREDENTIAL_ERRORS = [
  ...ACCOUNT_WRITE,
  "invalid_credentials",
  "invalid_mfa_code",
  "account_locked",
  "sso_required",
] as const satisfies readonly ExplorerApiErrorCode[];
const TWO_FACTOR_NOTE =
  "Served only while the release flag iam.two-factor is on (404 otherwise). The TOTP secret is answered once, with Cache-Control: no-store, and never stored by iam.";

const PLATFORM_NOTE =
  "A caller without the platform permission (an operator Administrator included) gets 404, checked before the body is read.";
const SSO_FAILURE_NOTE =
  "A refused or failed attempt redirects (303) to /login?error=<code> instead of answering JSON; only the rate limit (429) and an undecidable limiter (503) answer JSON.";

/** Routes of the `iam` module: authentication, invitations, `/me`, console users, IdP, docs access. */
export const IAM_ROUTES = [
  // ---- installation (public; ADR 0011) ---------------------------------------------------------
  {
    operationId: "getInstallation",
    method: "GET",
    path: "/installation",
    permission: "public",
    summary: "The operator environment of this installation and the base URL of its APIs.",
    description:
      "Read by the console before it renders (the login page included) to show the fixed environment badge. Needs no session; carries no secret. The values come from the installation's configuration, never from the hostname.",
    response: InstallationV1,
    successStatus: 200,
    publicMaxAgeSeconds: INSTALLATION_CACHE_MAX_AGE_SECONDS,
    errors: ["rate_limited", "server_error"],
  },
  {
    operationId: "getInstallationAdministration",
    method: "GET",
    path: "/installation/administration",
    permission: "authenticated",
    summary: "Whether this installation has the Platform area (STRUST_PLATFORM_ADMINISTRATION).",
    description:
      "enabled in the platform installation (and the internal customer test); disabled in a customer installation, where the console hides the Platform menu and shows the Integration screen to the holders of configuration.manage or credentials.manage (ADR 0022). Needs a live console session; answered no-store.",
    response: InstallationAdministrationV1,
    successStatus: 200,
    errors: ["unauthenticated", "server_error"],
  },
  // ---- authentication and invitation links (public) -------------------------------------------
  {
    operationId: "login",
    method: "POST",
    path: "/auth/login",
    permission: "public",
    summary: "Sign in with email and password; starts a console session.",
    description:
      "Sets the session cookie (HttpOnly) and the synchronizer token cookie; the body repeats the synchronizer token to send as X-CSRF-Token. An unknown email answers invalid_credentials.",
    body: LoginRequestV1,
    response: SessionStartedResponseV1,
    successStatus: 200,
    setsCookies: "console_session",
    loginStep: { response: LoginStepResponseV1, description: STEP_DESCRIPTION },
    errors: [
      ...PUBLIC_ERRORS,
      "invalid_credentials",
      "sso_required",
      "account_locked",
      "mfa_reset_pending",
    ],
  },
  {
    operationId: "completeLoginWithCode",
    method: "POST",
    path: "/auth/login/mfa",
    permission: "public",
    needsLoginStep: true,
    summary: "Enter the authenticator code of a password login; starts a console session.",
    description: `${LOGIN_STEP_NOTE} A wrong or replayed code answers invalid_mfa_code and counts as a failed login.`,
    body: MfaCodeRequestV1,
    response: SessionStartedResponseV1,
    successStatus: 200,
    setsCookies: "console_session",
    errors: [...LOGIN_STEP_ERRORS, "invalid_mfa_code", "account_locked"],
  },
  {
    operationId: "completeLoginWithNewPassword",
    method: "POST",
    path: "/auth/login/new-password",
    permission: "public",
    needsLoginStep: true,
    summary: "Replace the temporary password of a first login with a new one; continues the login.",
    description: `${LOGIN_STEP_NOTE} The new password must have 12+ characters and not be a common or breached password (weak_password, the step stays for another attempt). Audited password_changed. Then the login continues like any right password: a session (200), or the next step (202, e.g. mfa_setup_required for an administrator, with a new login-step cookie).`,
    body: NewPasswordRequestV1,
    response: SessionStartedResponseV1,
    successStatus: 200,
    setsCookies: "console_session",
    loginStep: { response: LoginStepResponseV1, description: STEP_DESCRIPTION },
    errors: [...LOGIN_STEP_ERRORS, "weak_password", "account_locked", "mfa_reset_pending"],
  },
  {
    operationId: "beginRequiredTwoFactorSetup",
    method: "POST",
    path: "/auth/login/two-factor-setup/begin",
    permission: "public",
    needsLoginStep: true,
    summary: "Start the mandatory set-up of an authenticator (QR code URI and secret, shown once).",
    description: `${LOGIN_STEP_NOTE} Answered with Cache-Control: no-store; the secret is never stored by iam.`,
    response: TwoFactorSetupBeginResponseV1,
    successStatus: 200,
    errors: LOGIN_STEP_ERRORS,
  },
  {
    operationId: "confirmRequiredTwoFactorSetup",
    method: "POST",
    path: "/auth/login/two-factor-setup/confirm",
    permission: "public",
    needsLoginStep: true,
    summary: "Confirm the new authenticator with its first code; starts a console session.",
    description: `${LOGIN_STEP_NOTE} A wrong code answers invalid_mfa_code and counts as a failed login.`,
    body: MfaCodeRequestV1,
    response: SessionStartedResponseV1,
    successStatus: 200,
    setsCookies: "console_session",
    errors: [...LOGIN_STEP_ERRORS, "invalid_mfa_code", "account_locked"],
  },
  {
    operationId: "requestPasswordReset",
    method: "POST",
    path: "/auth/password-reset",
    permission: "public",
    summary: 'Ask for a password reset code by email (self-service, "Forgot password").',
    description:
      "Always answers 202 with the same body, whatever the email: only an active console user who signs in with a password, is not required to use the organization's identity provider and has no pending two-factor reset gets a 6-digit code, emailed by the identity store after the answer. At most 3 codes per email per hour (further requests are answered the same, nothing is sent). Served only while the flag iam.password-reset is on.",
    body: PasswordResetRequestV1,
    response: PasswordResetAcceptedV1,
    successStatus: 202,
    successDescription: "Accepted: the same answer for every email (no account enumeration).",
    errors: [...PUBLIC_ERRORS],
  },
  {
    operationId: "confirmPasswordReset",
    method: "POST",
    path: "/auth/password-reset/confirm",
    permission: "public",
    summary: "Set a new password with the emailed reset code; starts no session.",
    description:
      "The code is accepted for 30 minutes after the request and once; a new request replaces it and 5 wrong codes cancel the reset. Every refused code (wrong, expired, used, replaced, or an email that got none) answers invalid_reset_code. On success every session of the user ends and the user logs in again (with the authenticator code when two-factor authentication is on). A 503 server_error with details.reason password_changed means the new password is set but ending the sessions or recording the reset failed: the user signs in with the new password (the sessions are still ended once the session store is back); any other 503 changed nothing. Served only while the flag iam.password-reset is on.",
    body: PasswordResetConfirmV1,
    successStatus: 204,
    errors: [...PUBLIC_ERRORS, "invalid_reset_code", "weak_password"],
  },
  {
    operationId: "startSso",
    method: "POST",
    path: "/auth/sso/start",
    permission: "public",
    summary: "Start an SSO login through the organization's identity provider (email in the body).",
    body: SsoStartRequestV1,
    response: SsoStartResponseV1,
    successStatus: 200,
    errors: [...PUBLIC_ERRORS, "sso_not_available"],
  },
  {
    operationId: "startInvitationSso",
    method: "GET",
    path: "/auth/sso/start",
    permission: "public",
    summary:
      "Accept an invitation through the organization's identity provider (browser navigation).",
    description: SSO_FAILURE_NOTE,
    query: SsoStartQueryV1,
    successStatus: 303,
    redirect: "The authorization URL of the organization's identity provider.",
    errors: ["rate_limited", "server_error"],
  },
  {
    operationId: "completeSso",
    method: "GET",
    path: "/auth/sso/callback",
    permission: "public",
    summary: "SSO callback of the user pool: completes the login and starts a console session.",
    description: `Sets the session and synchronizer token cookies and redirects (303) to the console. ${SSO_FAILURE_NOTE}`,
    query: SsoCallbackQueryV1,
    successStatus: 303,
    redirect: "The console (/).",
    setsCookies: "console_session",
    errors: ["rate_limited", "server_error"],
  },
  {
    operationId: "logout",
    method: "POST",
    path: "/auth/logout",
    permission: "authenticated",
    summary: "End the console session and clear its cookies.",
    description: "Idempotent: without a live session it also answers 204 and clears the cookies.",
    successStatus: 204,
    setsCookies: "console_session_cleared",
    errors: ["csrf_token_invalid", "server_error"],
  },
  {
    operationId: "getInvitation",
    method: "GET",
    path: "/invitations/{token}",
    permission: "public",
    summary: "What an invitation link opens: kind, email, expiry and sign-in method.",
    params: InvitationTokenParamsV1,
    response: InvitationViewV1,
    successStatus: 200,
    errors: ["not_found", ...INVITATION_UNUSABLE, "rate_limited", "server_error"],
  },
  {
    operationId: "acceptInvitation",
    method: "POST",
    path: "/invitations/{token}/accept",
    permission: "public",
    summary: "Accept an invitation with a new password; starts a console session.",
    description:
      "Sets the session and synchronizer token cookies. An organization that signs in through its identity provider answers sso_required (accept through GET /auth/sso/start instead). An invitation to an operator deactivated since it was issued answers 409 operator_deactivated; one to an operator no longer in the organization, 409 conflict (operator_not_linked). The email of a deactivated console user reactivates that same account with the invitation's access; an email whose sign-in account belongs to another console user answers 409 conflict (reason console_user_exists).",
    params: InvitationTokenParamsV1,
    body: AcceptInvitationRequestV1,
    response: SessionStartedResponseV1,
    successStatus: 200,
    setsCookies: "console_session",
    loginStep: {
      response: LoginStepResponseV1,
      description:
        "The password is set, but the account must set up an authenticator before its first session (mfa_setup_required): an administrator, or a two_factor_recovery invitation, which is consumed only when the new authenticator is confirmed.",
    },
    errors: [
      ...PUBLIC_ERRORS,
      "not_found",
      ...INVITATION_UNUSABLE,
      "weak_password",
      "sso_required",
      "conflict",
      "operator_deactivated",
    ],
  },
  // ---- the session's own console user ---------------------------------------------------------
  {
    operationId: "getMe",
    method: "GET",
    path: "/me",
    permission: "authenticated",
    summary: "Access context of the session (operators, roles, permissions) and display settings.",
    response: MeResponseV1,
    successStatus: 200,
    errors: ["unauthenticated", "server_error"],
  },
  {
    operationId: "updateMySettings",
    method: "PUT",
    path: "/me/settings",
    permission: "authenticated",
    summary: "Change the locale and/or time zone of the session's console user.",
    body: UpdateMySettingsRequestV1,
    response: MySettingsResponseV1,
    successStatus: 200,
    errors: ["unauthenticated", "csrf_token_invalid", "invalid_request", "server_error"],
  },
  {
    operationId: "getMyAccount",
    method: "GET",
    path: "/me/account",
    permission: "authenticated",
    summary:
      "Settings profile of the session's console user: email (read-only), organization, operators, sign-in and two-factor state.",
    response: AccountV1,
    successStatus: 200,
    errors: ["unauthenticated", "server_error"],
  },
  {
    operationId: "updateMyProfile",
    method: "PUT",
    path: "/me/profile",
    permission: "authenticated",
    summary:
      "Set or clear the display name of the session's console user (audited by field name only).",
    description: "The email is not editable: an email field answers invalid_request.",
    body: ProfileUpdateV1,
    response: AccountV1,
    successStatus: 200,
    errors: ["unauthenticated", "csrf_token_invalid", "invalid_request", "server_error"],
  },
  {
    operationId: "changeMyPassword",
    method: "POST",
    path: "/me/password",
    permission: "authenticated",
    summary:
      "Change the password (current password, current code when two-factor is on, new password).",
    description:
      "A wrong current password or code counts as a failed login toward the lockout. Every other session of the user ends; the current one continues. Single sign-on users answer sso_required.",
    body: PasswordChangeV1,
    successStatus: 204,
    errors: [...ACCOUNT_CREDENTIAL_ERRORS, "weak_password"],
  },
  {
    operationId: "beginMyTwoFactor",
    method: "POST",
    path: "/me/two-factor/begin",
    permission: "authenticated",
    summary:
      "Start setting up an authenticator after confirming the password (QR code URI and secret, shown once).",
    description: TWO_FACTOR_NOTE,
    body: TwoFactorEnrollV1,
    response: TwoFactorSetupBeginResponseV1,
    successStatus: 200,
    errors: [...ACCOUNT_CREDENTIAL_ERRORS, "conflict"],
  },
  {
    operationId: "confirmMyTwoFactor",
    method: "POST",
    path: "/me/two-factor/confirm",
    permission: "authenticated",
    summary:
      "Confirm the authenticator being set up (or replacing the old one) with its first code.",
    description: `${TWO_FACTOR_NOTE} A wrong code answers invalid_mfa_code and counts as a failed login; after 10 minutes or 3 wrong codes the set-up must start again (mfa_challenge_expired). A user who signs in only through single sign-on is answered sso_required first, whether or not a set-up is pending.`,
    body: MfaCodeRequestV1,
    successStatus: 204,
    errors: [
      ...ACCOUNT_WRITE,
      "invalid_mfa_code",
      "mfa_challenge_expired",
      "account_locked",
      "sso_required",
    ],
  },
  {
    operationId: "replaceMyTwoFactor",
    method: "POST",
    path: "/me/two-factor/replace",
    permission: "authenticated",
    summary: "Start replacing the authenticator (password and a current code of the old one).",
    description: TWO_FACTOR_NOTE,
    body: TwoFactorReplaceV1,
    response: TwoFactorSetupBeginResponseV1,
    successStatus: 200,
    errors: [...ACCOUNT_CREDENTIAL_ERRORS, "conflict"],
  },
  {
    operationId: "disableMyTwoFactor",
    method: "POST",
    path: "/me/two-factor/disable",
    permission: "authenticated",
    summary: "Remove an optional authenticator (password and a current code).",
    description: `${TWO_FACTOR_NOTE} A console user for whom two-factor is mandatory answers two_factor_required.`,
    body: TwoFactorDisableV1,
    successStatus: 204,
    errors: [...ACCOUNT_CREDENTIAL_ERRORS, "conflict", "two_factor_required"],
  },
  {
    operationId: "getDocsAccess",
    method: "GET",
    path: "/docs-access",
    permission: "authenticated",
    summary: "Grant access to the integration docs site (signed cookies, any console user).",
    description:
      "The CloudFront signed cookies are only in Set-Cookie (HttpOnly; Secure; SameSite=Strict), never in the body; the SPA then opens docs_url.",
    response: DocsAccessResponseV1,
    successStatus: 200,
    setsCookies: "docs_access",
    errors: ["unauthenticated", "server_error"],
  },
  // ---- console users of an operator (users.manage) --------------------------------------------
  {
    operationId: "listConsoleUsers",
    method: "GET",
    path: "/console-users",
    permission: "users.manage",
    summary: "Console users of the operator with their status, role and permissions.",
    query: OperatorScopeQueryV1,
    response: ConsoleUserListResponseV1,
    successStatus: 200,
    errors: MANAGE_READ,
  },
  {
    operationId: "inviteConsoleUser",
    method: "POST",
    path: "/console-users",
    permission: "users.manage",
    summary: "Invite a colleague with one role on the operator (single-use link token, 72 h).",
    body: InviteConsoleUserRequestV1,
    response: InvitationIssuedResponseV1,
    successStatus: 201,
    errors: [...MANAGE_WRITE, "conflict"],
  },
  {
    operationId: "listConsoleUserInvitations",
    method: "GET",
    path: "/console-users/invitations",
    permission: "users.manage",
    summary: "Invitations of the operator (never their tokens).",
    query: OperatorScopeQueryV1,
    response: InvitationListResponseV1,
    successStatus: 200,
    errors: MANAGE_READ,
  },
  {
    operationId: "revokeConsoleUserInvitation",
    method: "POST",
    path: "/console-users/invitations/{id}/revoke",
    permission: "users.manage",
    summary: "Revoke a pending invitation of the operator (audited).",
    params: IdParams,
    body: OperatorScopedRequestV1,
    successStatus: 204,
    errors: [...MANAGE_WRITE, ...INVITATION_UNUSABLE],
  },
  {
    operationId: "setConsoleUserRole",
    method: "PUT",
    path: "/console-users/{id}/roles",
    permission: "users.manage",
    summary: "Set the console user's role on the operator (null removes the access).",
    params: IdParams,
    body: SetConsoleUserRoleRequestV1,
    successStatus: 204,
    errors: [...MANAGE_WRITE, "conflict", "last_administrator", "own_account_protected"],
  },
  {
    operationId: "grantConsoleUserPermissions",
    method: "PUT",
    path: "/console-users/{id}/permissions",
    permission: "users.manage",
    summary: "Grant individual permissions on top of the role (location.precise.read).",
    params: IdParams,
    body: GrantConsoleUserPermissionsRequestV1,
    successStatus: 204,
    errors: MANAGE_WRITE,
  },
  {
    operationId: "deactivateConsoleUser",
    method: "POST",
    path: "/console-users/{id}/deactivate",
    permission: "users.manage",
    summary:
      "Deactivate a console user and end all of their sessions (never one's own account); a new invitation for the same email, or S-Trust's system reactivation in a customer installation, reactivates the account.",
    params: IdParams,
    body: OperatorScopedRequestV1,
    response: DeactivatedConsoleUserResponseV1,
    successStatus: 200,
    errors: [...MANAGE_WRITE, "last_administrator", "own_account_protected"],
  },
  {
    operationId: "unlockConsoleUser",
    method: "POST",
    path: "/console-users/{id}/unlock",
    permission: "users.manage",
    summary: "Unlock a console user locked by failed logins.",
    params: IdParams,
    body: OperatorScopedRequestV1,
    successStatus: 204,
    errors: [...MANAGE_WRITE, "conflict"],
  },
  {
    operationId: "issuePasswordSetupInvitation",
    method: "POST",
    path: "/console-users/{id}/password-setup-invitation",
    permission: "users.manage",
    summary: "Issue a password-setup invitation for an existing console user (72 h, single use).",
    params: IdParams,
    body: OperatorScopedRequestV1,
    response: InvitationIssuedResponseV1,
    successStatus: 201,
    errors: MANAGE_WRITE,
  },
  {
    operationId: "resetConsoleUserTwoFactor",
    method: "POST",
    path: "/console-users/{id}/two-factor/reset",
    permission: "users.manage",
    summary:
      "Reset a console user's two-factor authentication and issue a recovery invitation (72 h, single use, shown once).",
    description:
      "Needs users.manage on every operator of the user (otherwise 404). Removes the authenticator, ends the user's sessions and refuses their password login with mfa_reset_pending until the recovery invitation is used. Served only while the release flag iam.two-factor is on.",
    params: IdParams,
    body: OperatorScopedRequestV1,
    response: InvitationIssuedResponseV1,
    successStatus: 201,
    errors: [...MANAGE_WRITE, "conflict"],
  },
  // ---- organization identity provider (users.manage) ------------------------------------------
  {
    operationId: "getIdentityProvider",
    method: "GET",
    path: "/organization/identity-provider",
    permission: "users.manage",
    summary: "The organization's identity provider and the values to register in it.",
    description:
      "Needs users.manage on one operator of the caller's organization; can_manage is true only with users.manage on every operator of it. The client secret is always masked.",
    response: IdentityProviderSettingsResponseV1,
    successStatus: 200,
    errors: ["unauthenticated", "forbidden", "server_error"],
  },
  {
    operationId: "putIdentityProvider",
    method: "PUT",
    path: "/organization/identity-provider",
    permission: "users.manage",
    summary: "Register (201, draft) or update (200) the organization's identity provider.",
    description:
      "Needs users.manage on every operator of the caller's organization. An update requires a new successful test before activation; an omitted client_secret keeps the current one.",
    body: PutIdentityProviderRequestV1,
    response: IdentityProviderViewV1,
    successStatus: 200,
    alternativeSuccess: { status: 201, description: "Registered (status draft)." },
    errors: [...IDP_WRITE, "invalid_request", "conflict"],
  },
  {
    operationId: "testIdentityProvider",
    method: "POST",
    path: "/organization/identity-provider/test",
    permission: "users.manage",
    summary: "Test the organization's identity provider (required before activation).",
    response: IdentityProviderTestResponseV1,
    successStatus: 200,
    errors: IDP_WRITE,
  },
  {
    operationId: "activateIdentityProvider",
    method: "POST",
    path: "/organization/identity-provider/activate",
    permission: "users.manage",
    summary: "Activate the tested identity provider: the organization then signs in through SSO.",
    response: IdentityProviderViewV1,
    successStatus: 200,
    errors: [...IDP_WRITE, "conflict", "identity_provider_not_tested"],
  },
  {
    operationId: "deactivateIdentityProvider",
    method: "POST",
    path: "/organization/identity-provider/deactivate",
    permission: "users.manage",
    summary: "Deactivate the organization's identity provider.",
    response: IdentityProviderViewV1,
    successStatus: 200,
    errors: [...IDP_WRITE, "conflict"],
  },
  // ---- platform area: organizations, operator links, invitations ------------------------------
  {
    operationId: "listOrganizations",
    method: "GET",
    path: "/platform/organizations",
    permission: "platform.operators.manage",
    summary: "Organizations with their operators (active and deactivated), ordered by name.",
    description: PLATFORM_NOTE,
    query: PlatformOrganizationListQueryV1,
    response: PlatformOrganizationListResponseV1,
    successStatus: 200,
    errors: PLATFORM_READ,
  },
  {
    operationId: "getOrganizationAccess",
    method: "GET",
    path: "/platform/organizations/{id}/access",
    permission: "platform.operators.manage",
    summary:
      "The organization's access overview: first Administrator, active console users and Administrators, identity provider state.",
    description: `${PLATFORM_NOTE} An unknown organization answers 404 not_found. Read-only and not audited; never another console user's email, an invitation link or token, or identity provider metadata, issuer or client id.`,
    params: PlatformOrganizationParamsV1,
    response: PlatformOrganizationAccessV1,
    successStatus: 200,
    errors: PLATFORM_READ,
  },
  {
    operationId: "createOrganization",
    method: "POST",
    path: "/platform/organizations",
    permission: "platform.operators.manage",
    summary: "Create an organization.",
    description: PLATFORM_NOTE,
    body: CreateOrganizationRequestV1,
    response: OrganizationCreatedResponseV1,
    successStatus: 201,
    errors: PLATFORM_WRITE,
  },
  {
    operationId: "createOrganizationOperator",
    method: "POST",
    path: "/platform/organizations/{id}/operators",
    permission: "platform.operators.manage",
    summary:
      "Create an operator inside the organization, with the installation's environment (staging or production).",
    description: `${PLATFORM_NOTE} An unknown organization answers 404. A taken slug answers 409 conflict (reason operator_slug_taken). If the link failed after the operator was created (503), repeating the same slug and name completes it.`,
    params: IdParams,
    body: CreateOperatorRequestV1,
    response: OperatorLinkedResponseV1,
    successStatus: 201,
    errors: [...PLATFORM_WRITE, "conflict"],
  },
  {
    operationId: "inviteFirstAdministrator",
    method: "POST",
    path: "/platform/organizations/{id}/invitations",
    permission: "platform.operators.manage",
    summary: "Invite the first Administrator of an active operator of the organization.",
    description: `${PLATFORM_NOTE} Refused with 409 conflict (reason operator_not_linked) when the operator does not belong to the organization, and with 409 operator_deactivated for a deactivated operator. The link token is returned once.`,
    params: IdParams,
    body: InviteFirstAdministratorRequestV1,
    response: InvitationIssuedResponseV1,
    successStatus: 201,
    errors: [...PLATFORM_WRITE, "conflict", "operator_deactivated"],
  },
  {
    operationId: "invitePlatformAdministrator",
    method: "POST",
    path: "/platform/administrators/invitations",
    permission: "platform.operators.manage",
    summary: "Invite another Platform Administrator (platform-level, single use, 72 h).",
    description: `${PLATFORM_NOTE} The link token is returned once.`,
    body: InvitePlatformAdministratorRequestV1,
    response: InvitationIssuedResponseV1,
    successStatus: 201,
    errors: [...PLATFORM_WRITE, "conflict"],
  },
  {
    operationId: "listPlatformAdministrators",
    method: "GET",
    path: "/platform/administrators",
    permission: "platform.operators.manage",
    summary: "Platform Administrators (active first, then by email; at most 200).",
    description: `${PLATFORM_NOTE} Pending Platform Administrator invitations are listed by GET /platform/invitations.`,
    response: PlatformAdministratorListResponseV1,
    successStatus: 200,
    errors: PLATFORM_READ,
  },
  {
    operationId: "deactivatePlatformAdministrator",
    method: "POST",
    path: "/platform/administrators/{id}/deactivate",
    permission: "platform.operators.manage",
    summary:
      "Deactivate another Platform Administrator (their sessions end; only a new Platform Administrator invitation reactivates the account).",
    description: `${PLATFORM_NOTE} An id that is not a Platform Administrator (an operator user included) answers 404. Deactivating oneself or the last active Platform Administrator answers 409 last_platform_administrator. Audited platform_administrator_deactivated.`,
    params: PlatformAdministratorParamsV1,
    body: DeactivatePlatformAdministratorRequestV1,
    response: DeactivatedPlatformAdministratorResponseV1,
    successStatus: 200,
    errors: [...PLATFORM_WRITE, "last_platform_administrator"],
  },
  {
    operationId: "resetPlatformAdministratorTwoFactor",
    method: "POST",
    path: "/platform/administrators/{id}/two-factor/reset",
    permission: "platform.operators.manage",
    summary:
      "Reset another Platform Administrator's two-factor authentication and issue a recovery invitation (72 h, single use, shown once).",
    description: `${PLATFORM_NOTE} Removes the authenticator, ends the administrator's sessions and refuses their password login with mfa_reset_pending until the recovery invitation is used. Oneself, an id that is not an active Platform Administrator (an operator user included) answers 404; a Platform Administrator without a factor answers 409 conflict. Served only while the release flag iam.two-factor is on. Audited two_factor_reset.`,
    params: PlatformAdministratorParamsV1,
    body: ResetPlatformAdministratorTwoFactorRequestV1,
    response: InvitationIssuedResponseV1,
    successStatus: 201,
    errors: [...PLATFORM_WRITE, "conflict"],
  },
  {
    operationId: "getOperatorOrganization",
    method: "GET",
    path: "/platform/operators/{id}/organization",
    permission: "platform.operators.manage",
    summary: "The organization an operator is linked to (null while not linked).",
    description: PLATFORM_NOTE,
    params: IdParams,
    response: OperatorOrganizationResponseV1,
    successStatus: 200,
    errors: PLATFORM_READ,
  },
  {
    operationId: "listPendingPlatformInvitations",
    method: "GET",
    path: "/platform/invitations",
    permission: "platform.operators.manage",
    summary: "Pending platform invitations, newest first (never their link or token).",
    description: PLATFORM_NOTE,
    query: PlatformInvitationListQueryV1,
    response: PlatformInvitationListResponseV1,
    successStatus: 200,
    errors: PLATFORM_READ,
  },
  {
    operationId: "revokePlatformInvitation",
    method: "POST",
    path: "/platform/invitations/{id}/revoke",
    permission: "platform.operators.manage",
    summary: "Revoke a pending platform invitation (audited invitation_revoked).",
    description: PLATFORM_NOTE,
    params: PlatformInvitationParamsV1,
    body: RevokePlatformInvitationRequestV1,
    successStatus: 204,
    errors: [...PLATFORM_WRITE, ...INVITATION_UNUSABLE],
  },
] as const satisfies readonly ExplorerApiRoute[];

/** Routes of the `operator-auth` platform interface: operators, their environment, products, credentials. */
export const OPERATOR_AUTH_PLATFORM_ROUTES = [
  {
    operationId: "listOperators",
    method: "GET",
    path: "/platform/operators",
    permission: "platform.operators.manage",
    summary: "Operators with their environment, policy and products.",
    description: PLATFORM_NOTE,
    response: PlatformOperatorListResponseV1,
    successStatus: 200,
    errors: ["unauthenticated", "not_found", "server_error"],
  },
  {
    operationId: "getOperator",
    method: "GET",
    path: "/platform/operators/{id}",
    permission: "platform.operators.manage",
    summary: "One operator with its environment, policy and products.",
    description: PLATFORM_NOTE,
    params: PlatformOperatorParamsV1,
    response: PlatformOperatorV1,
    successStatus: 200,
    errors: ["unauthenticated", "not_found", "server_error"],
  },
  {
    operationId: "configureEnvironment",
    method: "PATCH",
    path: "/platform/operators/{id}/environment",
    permission: "platform.operators.manage",
    summary:
      "Change the TTL, rate limit, origins and EnvironmentPolicy of the operator's environment.",
    description: `${PLATFORM_NOTE} All changes apply together or none (400 names the fields).`,
    params: PlatformOperatorParamsV1,
    body: ConfigureEnvironmentRequestV1,
    response: PlatformEnvironmentResponseV1,
    successStatus: 200,
    errors: [...PLATFORM_WRITE, "operator_deactivated"],
  },
  {
    operationId: "putProduct",
    method: "PUT",
    path: "/platform/operators/{id}/environment/products/{product}",
    permission: "platform.operators.manage",
    summary: "Enable or disable a product and set its revalidation window.",
    description: `${PLATFORM_NOTE} A window above 1,800 s answers 400 invalid_request (reason invalid_revalidation_window); the previous window remains.`,
    params: PlatformProductParamsV1,
    body: PutProductRequestV1,
    response: PlatformEnvironmentResponseV1,
    successStatus: 200,
    errors: [...PLATFORM_WRITE, "operator_deactivated"],
  },
  {
    operationId: "deactivateOperator",
    method: "POST",
    path: "/platform/operators/{id}/deactivate",
    permission: "platform.operators.manage",
    summary:
      "Deactivate an operator (terminal): revoke its credentials, make it read-only, remove console access.",
    description: `${PLATFORM_NOTE} Needs a reason and the operator's slug typed as confirmation (400 invalid_request naming confirm_slug otherwise). Operators are never deleted: the record, the audit and the slug stay, and the purge date is recorded. An operator already deactivated answers 409 operator_deactivated. Behind the release flag operator-auth.operator-deactivation (404 while it is off).`,
    params: PlatformOperatorParamsV1,
    body: OperatorDeactivationRequestV1,
    response: PlatformOperatorV1,
    successStatus: 200,
    errors: [...PLATFORM_WRITE, "operator_deactivated"],
  },
  {
    operationId: "listCredentials",
    method: "GET",
    path: "/platform/operators/{id}/credentials",
    permission: "platform.credentials.manage",
    summary: "API credentials of the operator (secret masked).",
    description: PLATFORM_NOTE,
    params: PlatformOperatorParamsV1,
    response: PlatformCredentialListResponseV1,
    successStatus: 200,
    errors: ["unauthenticated", "not_found", "server_error"],
  },
  {
    operationId: "createCredential",
    method: "POST",
    path: "/platform/operators/{id}/credentials",
    permission: "platform.credentials.manage",
    summary: "Create an API credential: the only answer that carries the client_secret (once).",
    description: `${PLATFORM_NOTE} At most 2 active credentials per operator in the installation. The client_id carries the environment marker (oa_stg_ or oa_prd_); the console shows the secret once with the installation's environment and API base URL (GET /installation), to hand over out of band.`,
    params: PlatformOperatorParamsV1,
    body: CreateCredentialRequestV1,
    response: CreatedCredentialResponseV1,
    successStatus: 201,
    errors: [...PLATFORM_WRITE, "credential_limit_reached", "operator_deactivated"],
  },
  {
    operationId: "revokeCredential",
    method: "POST",
    path: "/platform/credentials/{id}/revoke",
    permission: "platform.credentials.manage",
    summary: "Revoke an API credential with an audited reason.",
    description: PLATFORM_NOTE,
    params: PlatformCredentialParamsV1,
    body: RevokeCredentialRequestV1,
    response: PlatformCredentialV1,
    successStatus: 200,
    errors: PLATFORM_WRITE,
  },
] as const satisfies readonly ExplorerApiRoute[];

// ---- Integration: the operator's configuration and API credentials (operator-auth) ------------

/** Operator configuration and credentials: per-operator isolation (`404`), the permission (`403`). */
const INTEGRATION_READ = MANAGE_READ;
const INTEGRATION_WRITE = [...MANAGE_WRITE, "operator_deactivated"] as const;
const INTEGRATION_NOTE =
  "The customer's Administrator, per operator (design D14 item 4): an operator the caller cannot access answers 404, one it can access without the permission 403 forbidden, a deactivated one 409 operator_deactivated. Same validation, limits and audit as the Platform routes; no route takes an environment (always the installation's).";

export const OPERATOR_AUTH_INTEGRATION_ROUTES = [
  {
    operationId: "getOperatorEnvironment",
    method: "GET",
    path: "/operators/{id}/environment",
    permission: "configuration.manage",
    summary: "The operator's environment: TTL, rate limit, origins, policy and products' windows.",
    description: INTEGRATION_NOTE,
    params: PlatformOperatorParamsV1,
    response: PlatformEnvironmentResponseV1,
    successStatus: 200,
    errors: [...INTEGRATION_READ, "operator_deactivated"],
  },
  {
    operationId: "configureOperatorEnvironment",
    method: "PATCH",
    path: "/operators/{id}/environment",
    permission: "configuration.manage",
    summary:
      "Change the TTL, rate limit, origins, retention tiers, jurisdictions, checks and thresholds.",
    description: `${INTEGRATION_NOTE} All changes apply together or none (400 names the fields).`,
    params: PlatformOperatorParamsV1,
    body: ConfigureEnvironmentRequestV1,
    response: PlatformEnvironmentResponseV1,
    successStatus: 200,
    errors: INTEGRATION_WRITE,
  },
  {
    operationId: "patchOperatorProductWindow",
    method: "PATCH",
    path: "/operators/{id}/environment/products/{product}",
    permission: "configuration.manage",
    summary: "Set the revalidation window of an enabled product (never enable or disable it).",
    description: `${INTEGRATION_NOTE} A window above 1,800 s answers 400 invalid_request (reason invalid_revalidation_window; Portaria SPA/MF 722) and the previous window remains. A body naming enabled, or a product that is not enabled, answers 403 forbidden: the contracted products are set by the control plane.`,
    params: PlatformProductParamsV1,
    body: PatchProductWindowRequestV1,
    response: PlatformEnvironmentResponseV1,
    successStatus: 200,
    errors: INTEGRATION_WRITE,
  },
  {
    operationId: "listOperatorCredentials",
    method: "GET",
    path: "/operators/{id}/credentials",
    permission: "credentials.manage",
    summary: "API credentials of the operator (secret masked).",
    description: INTEGRATION_NOTE,
    params: PlatformOperatorParamsV1,
    response: PlatformCredentialListResponseV1,
    successStatus: 200,
    errors: [...INTEGRATION_READ, "operator_deactivated"],
  },
  {
    operationId: "createOperatorCredential",
    method: "POST",
    path: "/operators/{id}/credentials",
    permission: "credentials.manage",
    summary: "Create an API credential: the only answer that carries the client_secret (once).",
    description: `${INTEGRATION_NOTE} At most 2 active credentials per operator (409 credential_limit_reached). The console shows the secret once to the Administrator who created it, with the installation's environment and API base URL (GET /installation); nobody at S-Trust ever sees it.`,
    params: PlatformOperatorParamsV1,
    body: CreateCredentialRequestV1,
    response: CreatedCredentialResponseV1,
    successStatus: 201,
    errors: [...INTEGRATION_WRITE, "credential_limit_reached"],
  },
  {
    operationId: "revokeOperatorCredential",
    method: "POST",
    path: "/credentials/{id}/revoke",
    permission: "credentials.manage",
    summary: "Revoke an API credential with an audited reason (rotation: create, move, revoke).",
    description: `${INTEGRATION_NOTE} Authorized on the credential's operator.`,
    params: PlatformCredentialParamsV1,
    body: RevokeCredentialRequestV1,
    response: PlatformCredentialV1,
    successStatus: 200,
    errors: INTEGRATION_WRITE,
  },
] as const satisfies readonly ExplorerApiRoute[];

// ---- Fleet tab of the platform installation (module fleet; change add-control-plane-console) ----

/** Fleet reads: a caller without `platform.fleet.manage` (or with the flag off) gets `404`. */
const FLEET_READ = [
  "unauthenticated",
  "not_found",
  "invalid_request",
  "server_error",
] as const satisfies readonly ExplorerApiErrorCode[];
/** Fleet changes with the step-up (password and current TOTP code, confirmed by iam). */
const FLEET_CHANGE = [
  ...FLEET_READ,
  "csrf_token_invalid",
  "conflict",
  "invalid_credentials",
  "invalid_mfa_code",
  "account_locked",
  "step_up_rate_limited",
] as const satisfies readonly ExplorerApiErrorCode[];
const FLEET_NOTE =
  "Platform installation only, behind the release flag fleet.console (404 while it is off, and in every customer installation). Needs platform.fleet.manage (404 otherwise). The registry or a dependency that cannot be read answers 503 server_error with details.reason naming it; nothing stale is answered as current.";
const FLEET_CHANGE_NOTE = `${FLEET_NOTE} Carries step_up (password and current TOTP code), confirmed by iam in the same call: a wrong password answers 401 invalid_credentials, a wrong or used code 401 invalid_mfa_code, the login lockout 423 account_locked, too many step-ups in flight or failed 429 step_up_rate_limited, an account without an authenticator 409 conflict (reason two_factor_not_enabled); a refused step-up records the refusal and changes nothing else. The requester is the authenticated Platform Administrator (console:<email>), never an input. A refusal of a rule answers 409 conflict with details.reason (FLEET_CONFLICT_REASONS). A dispatch that GitHub refuses answers 503 server_error (reason github, with the request_id of the request, which stays ready and unused: POST /platform/fleet/requests/{id}/dispatch retries it). A conflict carries details.explanation.`;

export const FLEET_ROUTES = [
  {
    operationId: "listFleetInstallations",
    method: "GET",
    path: "/platform/fleet/installations",
    permission: "platform.fleet.manage",
    summary:
      "Every customer installation of the registry, with its release, schema, state, drift and health, plus the read-only S-Trust staging row.",
    description: `${FLEET_NOTE} Destroyed installations only with include_destroyed=true.`,
    query: FleetListQueryV1,
    response: FleetListResponseV1,
    successStatus: 200,
    errors: FLEET_READ,
  },
  {
    operationId: "getFleetInstallation",
    method: "GET",
    path: "/platform/fleet/installations/{customer}/{environment}",
    permission: "platform.fleet.manage",
    summary:
      "One installation with the metrics of the last hour, its alarm states and its audit trail.",
    description: `${FLEET_NOTE} Metrics that cannot be read set metrics_error and an unknown health, never healthy. No player data, log line or trace.`,
    params: FleetInstallationParamsV1,
    response: FleetInstallationDetailV1,
    successStatus: 200,
    errors: FLEET_READ,
  },
  {
    operationId: "listFleetReleases",
    method: "GET",
    path: "/platform/fleet/releases",
    permission: "platform.fleet.manage",
    summary:
      "The release catalog: builds deployed by the S-Trust staging installation, newest first, with their published version.",
    description: `${FLEET_NOTE} A build without a version goes only to staging installations.`,
    response: FleetReleaseListResponseV1,
    successStatus: 200,
    errors: FLEET_READ,
  },
  {
    operationId: "publishFleetRelease",
    method: "POST",
    path: "/platform/fleet/releases/publish",
    permission: "platform.fleet.manage",
    summary:
      "Publish a build as a release vX.Y.Z: records the request and dispatches release-publish.yml, which creates the tag.",
    description: `${FLEET_CHANGE_NOTE} The version must be greater than every published one, on a build without one. Publishing deploys nothing.`,
    body: FleetPublishRequestV1,
    response: FleetRequestAcceptedV1,
    successStatus: 202,
    errors: FLEET_CHANGE,
  },
  {
    operationId: "planFleetRollout",
    method: "POST",
    path: "/platform/fleet/rollouts/plan",
    permission: "platform.fleet.manage",
    summary:
      "The plan of a release on chosen targets, computed on the registry as it is: per target from, to, database change and exclusion.",
    description: `${FLEET_NOTE} Reads only. blocked names why nothing can be launched.`,
    body: FleetPlanRequestV1,
    response: FleetPlanV1,
    successStatus: 200,
    errors: [...FLEET_READ, "csrf_token_invalid"],
  },
  {
    operationId: "launchFleetRollout",
    method: "POST",
    path: "/platform/fleet/rollouts",
    permission: "platform.fleet.manage",
    summary:
      "Launch the plan whose digest it names: records the rollout request and dispatches rollout.yml.",
    description: `${FLEET_CHANGE_NOTE} The plan is computed again: when it no longer matches the digest the answer is 409 conflict (reason plan_changed) and nothing is recorded. A plan with production targets needs confirm_production_count equal to their number (reason production_count_mismatch). A running or halted rollout refuses the launch (rollout_running, halted_rollout).`,
    body: FleetRolloutRequestV1,
    response: FleetRequestAcceptedV1,
    successStatus: 202,
    errors: FLEET_CHANGE,
  },
  {
    operationId: "getCurrentFleetRollout",
    method: "GET",
    path: "/platform/fleet/rollouts/current",
    permission: "platform.fleet.manage",
    summary:
      "The current rollout (running, halted or the last one), with its targets' progress and its audit trail.",
    description: FLEET_NOTE,
    response: FleetRolloutResponseV1,
    successStatus: 200,
    errors: FLEET_READ,
  },
  {
    operationId: "haltFleetRollout",
    method: "POST",
    path: "/platform/fleet/rollouts/{id}/halt",
    permission: "platform.fleet.manage",
    summary: "Halt the running rollout at once: no further target starts. No step-up.",
    description: `${FLEET_NOTE} Writes the halt item of the rollout, never its state; an upgrade already in its migration or Helm upgrade finishes. A rollout that is not the running one answers 409 conflict (reason rollout_not_running). Halting twice is a no-op.`,
    params: FleetRolloutParamsV1,
    body: FleetHaltRequestV1,
    response: FleetRolloutResponseV1,
    successStatus: 200,
    errors: [...FLEET_READ, "csrf_token_invalid", "conflict"],
  },
  {
    operationId: "clearFleetRollout",
    method: "POST",
    path: "/platform/fleet/rollouts/{id}/clear",
    permission: "platform.fleet.manage",
    summary:
      "Clear a halted rollout after the fix: records a rollout-clear request and dispatches rollout.yml clear.",
    description: FLEET_CHANGE_NOTE,
    params: FleetRolloutParamsV1,
    body: FleetStepUpRequestV1,
    response: FleetRequestAcceptedV1,
    successStatus: 202,
    errors: FLEET_CHANGE,
  },
  {
    operationId: "createFleetCustomer",
    method: "POST",
    path: "/platform/fleet/customers",
    permission: "platform.fleet.manage",
    summary:
      "Create a customer: records a customer-create request with the inputs of customer-create.yml and dispatches it.",
    description: `${FLEET_CHANGE_NOTE} The inputs are checked with the registry's rules before anything is recorded (reserved or malformed slug: 400 invalid_request; an existing customer: customer_exists; an account of another customer: account_taken; production with a build that is not published: not_published).`,
    body: FleetCreateCustomerRequestV1,
    response: FleetRequestAcceptedV1,
    successStatus: 202,
    errors: FLEET_CHANGE,
  },
  {
    operationId: "configureFleetCustomer",
    method: "POST",
    path: "/platform/fleet/customers/{customer}/configure",
    permission: "platform.fleet.manage",
    summary:
      "Configure a customer's operators: records a customer-configure request (new operators, more products, deactivations) and dispatches customer-configure.yml.",
    description: `${FLEET_CHANGE_NOTE} The changes are checked with the registry's rules before anything is recorded (no change, an operator named twice, an unknown product or an empty reason: 400 invalid_request; an unknown customer: customer_not_found). The workflow runs a read-only check in every named installation before any change, then the provisioning Job as the system (cause configure).`,
    params: FleetCustomerParamsV1,
    body: FleetConfigureCustomerRequestV1,
    response: FleetRequestAcceptedV1,
    successStatus: 202,
    errors: FLEET_CHANGE,
  },
  {
    operationId: "destroyFleetCustomer",
    method: "POST",
    path: "/platform/fleet/customers/{customer}/destroy",
    permission: "platform.fleet.manage",
    summary:
      "Destroy a customer: with production the request waits for a second Platform Administrator's approval; otherwise customer-destroy.yml is dispatched at once.",
    description: `${FLEET_CHANGE_NOTE} confirm is the slug, or "<customer> production" when production is included (reason confirmation_mismatch otherwise, recorded as a refusal). Production must be included while the customer has a production installation, and when it ever had one and has an AWS account, since the destroy closes the account and that ends what production retained (reason production_required, recorded as a refusal); a destroy that includes production for a customer without any is refused too (reason no_production).`,
    params: FleetCustomerParamsV1,
    body: FleetDestroyCustomerRequestV1,
    response: FleetRequestAcceptedV1,
    successStatus: 202,
    errors: FLEET_CHANGE,
  },
  {
    operationId: "listFleetRequests",
    method: "GET",
    path: "/platform/fleet/requests",
    permission: "platform.fleet.manage",
    summary: "The console's requests, newest first (the approvals list: status=pending-approval).",
    description: FLEET_NOTE,
    query: FleetRequestListQueryV1,
    response: FleetRequestListResponseV1,
    successStatus: 200,
    errors: FLEET_READ,
  },
  {
    operationId: "getFleetRequest",
    method: "GET",
    path: "/platform/fleet/requests/{id}",
    permission: "platform.fleet.manage",
    summary: "One console request, by its id (the summary a dispatch again shows).",
    description: `${FLEET_NOTE} An unknown request answers 404 not_found.`,
    params: FleetRequestParamsV1,
    response: FleetRequestResponseV1,
    successStatus: 200,
    errors: FLEET_READ,
  },
  {
    operationId: "approveFleetRequest",
    method: "POST",
    path: "/platform/fleet/requests/{id}/approve",
    permission: "platform.fleet.manage",
    summary: "Approve a production destroy as a second Platform Administrator, then dispatch it.",
    description: `${FLEET_CHANGE_NOTE} The requester cannot approve their own request (self_approval); an expired request (request_expired) or one that is not pending (request_not_pending) dispatches nothing.`,
    params: FleetRequestParamsV1,
    body: FleetStepUpRequestV1,
    response: FleetRequestAcceptedV1,
    successStatus: 202,
    errors: FLEET_CHANGE,
  },
  {
    operationId: "rejectFleetRequest",
    method: "POST",
    path: "/platform/fleet/requests/{id}/reject",
    permission: "platform.fleet.manage",
    summary: "Reject a pending request with a reason.",
    description: FLEET_CHANGE_NOTE,
    params: FleetRequestParamsV1,
    body: FleetRejectRequestV1,
    response: FleetRequestV1,
    successStatus: 200,
    errors: FLEET_CHANGE,
  },
  {
    operationId: "dispatchFleetRequest",
    method: "POST",
    path: "/platform/fleet/requests/{id}/dispatch",
    permission: "platform.fleet.manage",
    summary:
      "Dispatch again a request whose dispatch failed: ready, approved when it needs it, not expired and not used.",
    description: `${FLEET_CHANGE_NOTE} A request that a run already used, or whose run already exists, answers 409 conflict (reason request_used); one that is not ready request_not_pending; an expired one request_expired.`,
    params: FleetRequestParamsV1,
    body: FleetStepUpRequestV1,
    response: FleetRequestAcceptedV1,
    successStatus: 202,
    errors: FLEET_CHANGE,
  },
  {
    operationId: "listFleetAudit",
    method: "GET",
    path: "/platform/fleet/audit",
    permission: "platform.fleet.manage",
    summary:
      "The console audit trail: requests, approvals, rejections, refusals, halts and publications, newest first, 100 a page.",
    description: `${FLEET_NOTE} Filterable by staff member, customer, installation, rollout, action and period. Read-only: audit items are never changed or deleted.`,
    query: FleetAuditQueryV1,
    response: FleetAuditResponseV1,
    successStatus: 200,
    errors: FLEET_READ,
  },
] as const satisfies readonly ExplorerApiRoute[];

/** The module that serves a route (`x-strust-module` and tag of the OpenAPI operation). */
export type ExplorerApiModule = "explorer" | "iam" | "operator-auth" | "fleet";

export interface ModuleExplorerApiRoute extends ExplorerApiRoute {
  readonly module: ExplorerApiModule;
}

/** Every route of `/explorer/v1`, tagged with the module that serves it. */
export const EXPLORER_API_ROUTES: readonly ModuleExplorerApiRoute[] = [
  ...EXPLORER_ROUTES.map((route) => ({ ...route, module: "explorer" as const })),
  ...IAM_ROUTES.map((route) => ({ ...route, module: "iam" as const })),
  ...OPERATOR_AUTH_PLATFORM_ROUTES.map((route) => ({ ...route, module: "operator-auth" as const })),
  ...OPERATOR_AUTH_INTEGRATION_ROUTES.map((route) => ({
    ...route,
    module: "operator-auth" as const,
  })),
  ...FLEET_ROUTES.map((route) => ({ ...route, module: "fleet" as const })),
];
